import { useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";
import {
  useToteSubscription,
  usePickSubscription,
  usePTLSubscription,
  useGridV2Subscription,
  useHeartbeatSubscription,
  useAndonSubscription,
  useBinFlagSubscription
} from "~/lib/signalr";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";
import {
  ToteEventDto,
  SignalRPickEventDto,
  PTLEventDto,
  AutostoreEvent,
  AndonEvent,
  HeartbeatEvent,
  BinFlagEvent
} from "~/types/api";

import {
  portIdFromEvent,
  gridIdFromEvent,
  binIdFromEvent,
  workstationIdFromEvent,
  removeStalePongsAfterDelay
} from "./andon.logic";

import {
  setHandRaised,
  setPortStatus,
  setPortHasBins,
  setGridSystemMode,
  selectAndonEventSaveLimit,
  addEventToHistory,
  setCurrentPongId,
  selectBoardPortToWorkstationId
} from "./andon.slice";
import { AndonStatus } from "./andon.types";
import { calculateAndonPortKey, getAndonPortEntry } from "./andon.utils";
import {
  handleHeartbeatPing,
  handleHeartbeatPong
} from "./heartbeat/heartbeatHandlers";

export function EventListeningComponent() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { an_andonFeaturesEnabled } = useAppSelector(selectServerConfig);

  const batchRef = useRef<string | null>(null);
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const authMethod = useAppSelector((state) => state.login.authMethod);
  const binsMap = useAppSelector((state) => state.andon.portHasBinsByPortKey);
  const eventSaveLimit = useAppSelector(selectAndonEventSaveLimit);
  const userFc = useAppSelector((state) => state.store.usersFulfillmentCenter);
  const boardPortToWorkstationId = useAppSelector(
    selectBoardPortToWorkstationId
  );

  const { data: workstations } = useGetWorkstationsQuery(undefined, {
    skip: !userFc
  });

  const portToWorkstationIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const ws of workstations ?? []) {
      const gridId = ws.autostoreGridId;
      for (const port of ws.ports) {
        map[calculateAndonPortKey(gridId, port.portId)] = ws.id;
        map[String(port.portId)] = ws.id;
        if (port.parentPortId != null) {
          map[calculateAndonPortKey(gridId, port.parentPortId)] = ws.id;
          map[String(port.parentPortId)] = ws.id;
        }
      }
    }
    return { ...map, ...boardPortToWorkstationId };
  }, [workstations, boardPortToWorkstationId]);

  const workstationIdForPort = (portId?: string, gridId?: string | null) => {
    if (!portId) return undefined;
    return getAndonPortEntry(portToWorkstationIdMap, portId, gridId);
  };

  const isOnServiceSupport = location.pathname.includes("service-support");
  const shouldListenToExpandedEvents =
    an_andonFeaturesEnabled && (authMethod === "ANDON" || isOnServiceSupport);

  const gridSubscription = (autostoreEvent: AutostoreEvent) => {
    if (!an_andonFeaturesEnabled) return;

    switch (autostoreEvent.case) {
      case "PortMode": {
        const portId = autostoreEvent.event.portId.toString();
        const gridId = autostoreEvent.event.gridId;

        if (eventSaveLimit > 0) {
          dispatch(
            addEventToHistory({
              hub: "grid",
              portId,
              gridId,
              event: autostoreEvent,
              timestamp: new Date(),
              workstationId: workstationIdForPort(portId, gridId)
            })
          );
        }

        return;
      }

      case "PortError": {
        const portId = autostoreEvent.event.portId.toString();
        const gridId = autostoreEvent.event.gridId;
        const status: AndonStatus = autostoreEvent.event.hasError
          ? "bad"
          : "good";

        dispatch(setPortStatus({ portId, status, gridId }));

        if (eventSaveLimit > 0) {
          dispatch(
            addEventToHistory({
              hub: "grid",
              portId,
              gridId,
              status,
              event: autostoreEvent,
              timestamp: new Date(),
              workstationId:
                workstationIdFromEvent(autostoreEvent) ||
                workstationIdForPort(portId, gridId)
            })
          );
        }

        return;
      }

      case "HandToggled": {
        dispatch(
          setHandRaised({
            workstationId: autostoreEvent.event.workstationId,
            handRaised: autostoreEvent.event.raiseHand,
            // we should put the timestamp on the event perhaps?
            timestamp: autostoreEvent.event.raiseHand
              ? new Date().toISOString()
              : undefined,
            // Include request type if available in the event
            requestType:
              "requestType" in autostoreEvent.event
                ? (autostoreEvent.event.requestType as string)
                : undefined
          })
        );

        if (eventSaveLimit > 0) {
          dispatch(
            addEventToHistory({
              hub: "grid",
              gridId: gridIdFromEvent(autostoreEvent),
              event: autostoreEvent,
              timestamp: new Date(),
              workstationId: autostoreEvent.event.workstationId
            })
          );
        }
        return;
      }

      case "SystemModeChanged": {
        dispatch(
          setGridSystemMode({
            gridId: autostoreEvent.event.gridId,
            systemMode: autostoreEvent.event.systemMode
          })
        );

        if (eventSaveLimit > 0) {
          dispatch(
            addEventToHistory({
              hub: "grid",
              gridId: autostoreEvent.event.gridId,
              event: autostoreEvent,
              timestamp: new Date()
            })
          );
        }
        return;
      }

      case "BinModeChange": {
        const { binMode, portId, binId, gridId } = autostoreEvent.event;
        if (!portId) return;

        const portIdStr = portId.toString();
        const binsKey = gridId
          ? calculateAndonPortKey(gridId, portIdStr)
          : portIdStr;

        const existingBins = binsMap[binsKey] || [];
        const newBins =
          binMode === "O"
            ? Array.from(new Set([...existingBins, binId]))
            : existingBins.filter((b) => b !== binId);

        dispatch(setPortHasBins({ portId: portIdStr, bins: newBins, gridId }));

        if (eventSaveLimit > 0) {
          dispatch(
            addEventToHistory({
              hub: "grid",
              gridId,
              portId: portIdStr,
              binId: autostoreEvent.event.binId.toString(),
              event: autostoreEvent,
              timestamp: new Date()
            })
          );
        }

        return;
      }
      default: {
        if (!shouldListenToExpandedEvents) return;
        const portId = portIdFromEvent(autostoreEvent);
        const gridId = gridIdFromEvent(autostoreEvent);
        const workstationId = workstationIdFromEvent(autostoreEvent);

        if (eventSaveLimit > 0) {
          dispatch(
            addEventToHistory({
              hub: "grid",
              portId,
              gridId,
              binId: binIdFromEvent(autostoreEvent),
              event: autostoreEvent,
              timestamp: new Date(),
              workstationId:
                workstationId || workstationIdForPort(portId, gridId)
            })
          );
        }
        return;
      }
    }
  };
  useGridV2Subscription(gridSubscription);

  const pickSubscription = (autostoreEvent: SignalRPickEventDto) => {
    if (!shouldListenToExpandedEvents) return;

    const portId = portIdFromEvent(autostoreEvent);
    const gridId = gridIdFromEvent(autostoreEvent);

    if (eventSaveLimit > 0) {
      dispatch(
        addEventToHistory({
          hub: "pick",
          portId: portIdFromEvent(autostoreEvent),
          binId: binIdFromEvent(autostoreEvent),
          event: autostoreEvent,
          timestamp: new Date(),
          workstationId:
            workstationIdFromEvent(autostoreEvent) ||
            workstationIdForPort(portId, gridId)
        })
      );
    }
  };

  usePickSubscription(pickSubscription);

  const toteSubscription = (autostoreEvent: ToteEventDto) => {
    if (!shouldListenToExpandedEvents) return;

    const portId = portIdFromEvent(autostoreEvent);
    const gridId = gridIdFromEvent(autostoreEvent);

    if (eventSaveLimit > 0) {
      dispatch(
        addEventToHistory({
          hub: "tote",
          portId: portIdFromEvent(autostoreEvent),
          gridId: gridIdFromEvent(autostoreEvent),
          event: autostoreEvent,
          timestamp: new Date(),
          workstationId:
            workstationIdFromEvent(autostoreEvent) ||
            workstationIdForPort(portId, gridId)
        })
      );
    }
  };

  useToteSubscription(toteSubscription);

  const ptlSubscription = (autostoreEvent: PTLEventDto) => {
    if (!shouldListenToExpandedEvents) return;

    const portId = portIdFromEvent(autostoreEvent);
    const gridId = gridIdFromEvent(autostoreEvent);

    if (eventSaveLimit > 0) {
      dispatch(
        addEventToHistory({
          hub: "ptl",
          portId: portIdFromEvent(autostoreEvent),
          gridId: gridIdFromEvent(autostoreEvent),
          event: autostoreEvent,
          timestamp: new Date(),
          workstationId:
            workstationIdFromEvent(autostoreEvent) ||
            workstationIdForPort(portId, gridId)
        })
      );
    }
  };
  usePTLSubscription(ptlSubscription);

  const heartbeatSubscription = (data: HeartbeatEvent) => {
    if (!an_andonFeaturesEnabled) return;

    switch (data.case) {
      case "Ping":
        // only send a ping if there is a workstation assigned
        if (!siteWorkstation) return;
        void (async () => {
          const batch = Date.now().toString();
          batchRef.current = batch;
          dispatch(setCurrentPongId(batch));
          // schedule stale‐cleanup
          void dispatch(removeStalePongsAfterDelay(batch));
          await handleHeartbeatPing({
            data,
            siteWorkstationId: siteWorkstation.id,
            workstationDeviceId: siteWorkstation.deviceId || null
          });
        })();
        break;

      case "Pong":
        // only listen to pongs if there is no workstation assigned
        if (siteWorkstation) return;
        handleHeartbeatPong({
          data,
          dispatch,
          batchTimestamp: batchRef.current
        });
        break;
    }
  };
  useHeartbeatSubscription(heartbeatSubscription);

  const andonSubscription = (andonEvent: AndonEvent) => {
    if (!an_andonFeaturesEnabled) return;

    dispatch(
      setHandRaised({
        workstationId: andonEvent.event.workstationId,
        handRaised: andonEvent.event.raiseHand,
        // we should put the timestamp on the event perhaps?
        timestamp: andonEvent.event.raiseHand
          ? new Date().toISOString()
          : undefined,
        // Include request type if available in the event
        requestType:
          "requestType" in andonEvent.event
            ? (andonEvent.event.requestType as string)
            : undefined
      })
    );

    if (eventSaveLimit > 0) {
      dispatch(
        addEventToHistory({
          hub: "andon",
          gridId: gridIdFromEvent(andonEvent),
          event: andonEvent,
          timestamp: new Date(),
          workstationId: andonEvent.event.workstationId
        })
      );
    }
    return;
  };
  useAndonSubscription(andonSubscription);

  const binFlagSubscription = (binFlagEvent: BinFlagEvent) => {
    if (!shouldListenToExpandedEvents) return;
    const portId = portIdFromEvent(binFlagEvent);
    const gridId = gridIdFromEvent(binFlagEvent);
    const workstationId = workstationIdFromEvent(binFlagEvent);

    if (eventSaveLimit > 0) {
      dispatch(
        addEventToHistory({
          hub: "bin-flag",
          portId,
          gridId,
          binId: binIdFromEvent(binFlagEvent),
          event: binFlagEvent,
          timestamp: new Date(),
          workstationId: workstationId || workstationIdForPort(portId, gridId)
        })
      );
    }
    return;
  };
  useBinFlagSubscription(binFlagSubscription);

  return null;
}

export default EventListeningComponent;
